/** Promote-with-evidence dialog (SA-36 spec §6): the tenant channel-mode
 * gate on a pending candidate version — requires a run reference (URL or
 * ID) plus an explicit attestation checkbox before the promote POST is
 * allowed. Scaffolded from ImportVersionDialog.tsx. */
import { useState } from 'react'
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '@/components/ui'
import { usePromoteWorkflowVersion } from '@/hooks/useWorkflowVersions'
import { extractPublishFindings } from '../configure/publishFindings'
import { refusalMessage } from './refusalMessage'

export function PromoteCandidateDialog({
  workflowId,
  onClose,
}: {
  workflowId: string
  onClose: () => void
}) {
  const [runUrl, setRunUrl] = useState('')
  const [runId, setRunId] = useState('')
  const [notes, setNotes] = useState('')
  const [attested, setAttested] = useState(false)
  const promote = usePromoteWorkflowVersion(workflowId)

  const valid = (runUrl.trim() !== '' || runId.trim() !== '') && attested
  const findings = promote.isError ? extractPublishFindings(promote.error) : null

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent size="sm" showClose={false}>
        <ModalHeader><ModalTitle>Promote candidate</ModalTitle></ModalHeader>
        <ModalBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Promoting makes this candidate the current version. Attach the sweep evidence
            that justifies it.
          </p>
          <label className="block text-sm">
            Sweep run URL
            <input
              aria-label="Sweep run URL"
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              value={runUrl}
              onChange={(e) => setRunUrl(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Run ID
            <input
              aria-label="Run ID"
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              value={runId}
              onChange={(e) => setRunId(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Notes (optional)
            <textarea
              aria-label="Notes"
              rows={2}
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
            />
            The tenant scenario suite passed against this candidate
          </label>
          {promote.isError && (
            findings ? (
              <ul className="text-sm text-destructive space-y-0.5">
                {findings.map((f, i) => (
                  <li key={i}>
                    <span className="font-mono">{f.path}</span>: {f.message}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-destructive">
                {refusalMessage(promote.error)
                  ?? (promote.error instanceof Error ? promote.error.message : 'Promote failed.')}
              </p>
            )
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={promote.isPending || !valid}
            isLoading={promote.isPending}
            onClick={() =>
              promote.mutate(
                {
                  run_url: runUrl.trim() || undefined,
                  run_id: runId.trim() || undefined,
                  verdict: 'pass',
                  notes: notes.trim() || undefined,
                },
                { onSuccess: onClose },
              )
            }
          >
            {promote.isPending ? 'Promoting…' : 'Promote version'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
